import { useCallback } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import {
	isIntentDetectionSuccess,
	jiraIntentDetectionQuery,
	KnowledgeDiscoveryJiraIntentDetectionModel,
	KnowledgeDiscoveryJiraSearchExperience,
} from '@atlassian/search-client';

import { IntentMode } from '../../../../common/utils/intent-detection';
import { onIntentDetectionResponse } from '../../../../common/utils/quick-find/events/intent-detection-response';
import { useQuickFindAnalytics } from '../../../../controllers/quick-find/analytics';
import { useSearchSessionId } from '../../../../controllers/store/bootstrap';
import {
	useCloudId,
	useOrgId,
	usePrimaryProduct,
} from '../../../../controllers/store/bootstrap/accessors';

/**
 * Real intent detection via @atlassian/search-client.
 */
export const useIntentDetectionService = ({
	defaultMode = IntentMode.GNIN,
}: { defaultMode?: IntentMode } = {}) => {
	const [cloudId] = useCloudId();
	const [orgId] = useOrgId();
	const [primaryProduct] = usePrimaryProduct();
	const [searchSessionId] = useSearchSessionId();
	const { fireAnalyticsEvent } = useQuickFindAnalytics();

	const fireAnalytics = useCallback(
		(action: 'success' | 'fail') => {
			fireAnalyticsEvent(onIntentDetectionResponse({ action, attributes: { searchSessionId } }));
		},
		[searchSessionId, fireAnalyticsEvent],
	);

	return useCallback(
		async (query: string): Promise<IntentMode> => {
			// create siteARI based on primary product
			const siteARI = `ari:cloud:${primaryProduct}::site/${cloudId}`;
			// plex code uses "jira" as the primary product key but we need to use "jira-software" instead for intent detection
			const jiraSiteARI = `ari:cloud:jira-software::site/${cloudId}`;
			const siteId =
				primaryProduct === 'jira' && fg('kd_fix_jira_intent_siteid') ? jiraSiteARI : siteARI;

			try {
				const response = await jiraIntentDetectionQuery({
					query,
					siteId,
					orgId: orgId ?? '',
					experience: KnowledgeDiscoveryJiraSearchExperience.QUICK_FIND,
					model: KnowledgeDiscoveryJiraIntentDetectionModel.GNIN_HEURISTIC,
				});

				const result = response.data?.knowledgeDiscovery.jiraIntentDetection;
				if (!isIntentDetectionSuccess(result)) {
					fireAnalytics('fail');
					return defaultMode;
				}

				fireAnalytics('success');
				return result.searchClassification?.type || defaultMode;
			} catch {
				fireAnalytics('fail');
				return defaultMode;
			}
		},
		[cloudId, orgId, primaryProduct, defaultMode, fireAnalytics],
	);
};
